import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type { components } from "@aeriosiq/types";

/**
 * R58-L-02 — image-review-state visit hook.
 *
 * Fires a debounced POST `/api/lyse/v1/image-review-states` when the
 * active image changes. OQ-7 (a) invariant: one row per
 * `(image_id, data_acquisition_id, reviewed_by_user_id)`; re-visits bump
 * `reviewed_at` only. The hook is decoupled from the FE's active-image
 * store — callers pass `(activeImageId, dataAcquisitionId)` so unit
 * tests can drive the hook without mounting the whole AnalysisFeature
 * tree.
 *
 * Debounce: 300 ms. A fast arrow-key burst (10 presses in 1 s) collapses
 * to at most one POST per distinct image — the timer is restarted on
 * every activation; when it fires, the LATEST (image, DA) tuple is
 * sent. If the user lands on image A → B → A within the window, the
 * POST that lands is for A (only the final tuple is sent — the
 * intermediate B visit is intentionally dropped per AC3 of R58-L-02).
 *
 * Invalidation: on settle, invalidates two TanStack Query keys so
 * downstream consumers (R58-L-03 progress bar, R58-L-04 Unseen filter)
 * re-fetch:
 *   - `["review-progress", dataAcquisitionId, "self"]`
 *   - `["unseen-images", dataAcquisitionId, "self"]`
 *
 * Contract pin: `lyse/v1@5.7.0`.
 */

const DEBOUNCE_MS = 300;

type Body = components["schemas"]["UpsertImageReviewStateBody"];
type Response = components["schemas"]["UpsertImageReviewStateResponse"];

export interface UseImageReviewStateOptions {
  /** Currently active image id. `null` ⇒ no visit to record. */
  activeImageId: string | null;
  /** The DA the image lives under. `null` ⇒ pre-data; no visit. */
  dataAcquisitionId: string | null;
  /**
   * Optional override of the debounce window. The default 300 ms is the
   * R58-L-02 PM lock; tests override to 0 for synchronous flushing.
   */
  debounceMs?: number;
  /**
   * Optional `false` toggle to disable the hook (e.g. while the DA is
   * in a non-review phase). When `false`, no POST is fired and any
   * pending debounce is cancelled. Defaults to `true`.
   */
  enabled?: boolean;
}

export interface UseImageReviewStateResult {
  /** True while the most recent POST is in flight. */
  isPending: boolean;
  /** Last successful response, or `null` before the first settle. */
  lastResponse: Response | null;
  /**
   * Force-flush the pending debounce. Mostly used by tests + the
   * AnalysisFeature unmount path, so a navigation that interrupts the
   * window doesn't drop the gesture.
   */
  flush: () => void;
}

export function useImageReviewState({
  activeImageId,
  dataAcquisitionId,
  debounceMs = DEBOUNCE_MS,
  enabled = true,
}: UseImageReviewStateOptions): UseImageReviewStateResult {
  const api = useApi();
  const qc = useQueryClient();
  const lastResponseRef = useRef<Response | null>(null);

  const mutation = useMutation<Response, Error, Body>({
    mutationFn: async (body) => {
      // SB6-ANA-IRS-01: use relative path. The api-client baseURL already
      // includes the /api/lyse/v1 prefix; sending the absolute path here
      // produced a doubled URL `/api/lyse/v1/api/lyse/v1/image-review-states`
      // that 404'd on every navigation, which is why progress and the
      // image-viewed check column never populated.
      const res = await api.post<Response>("/image-review-states", body);
      return res.data;
    },
    onSuccess: (data, vars) => {
      lastResponseRef.current = data;
      // Round-2 consumers: R58-L-03 progress bar and R58-L-04 Unseen
      // filter. Invalidate the per-DA, per-self keys so the next render
      // re-fetches. The key shape is documented here so the L-03/L-04
      // orchestrators can match.
      void qc.invalidateQueries({
        queryKey: ["review-progress", vars.dataAcquisitionId, "self"],
      });
      void qc.invalidateQueries({
        queryKey: ["unseen-images", vars.dataAcquisitionId, "self"],
      });
    },
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<Body | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const body = pendingRef.current;
    pendingRef.current = null;
    if (!body) return;
    mutation.mutate(body);
  }, [mutation]);

  // Schedule a POST on every (activeImageId, dataAcquisitionId) change.
  // The effect's stable dependency tuple is the schedule key — when
  // either id flips, we restart the timer with the new payload. We
  // intentionally don't list `mutation` in the deps: TanStack Query's
  // `useMutation` returns a fresh object on every render, which would
  // re-arm the timer constantly. The ref captures the latest mutator.
  const mutateRef = useRef(mutation.mutate);
  useEffect(() => {
    mutateRef.current = mutation.mutate;
  }, [mutation.mutate]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingRef.current = null;
      return;
    }
    if (!activeImageId || !dataAcquisitionId) {
      // No-op for null inputs — caller hasn't selected an image yet.
      return;
    }
    const body: Body = {
      imageId: activeImageId,
      dataAcquisitionId,
    };
    pendingRef.current = body;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    if (debounceMs <= 0) {
      timerRef.current = null;
      pendingRef.current = null;
      mutateRef.current(body);
      return;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) mutateRef.current(pending);
    }, debounceMs);
  }, [activeImageId, dataAcquisitionId, debounceMs, enabled]);

  // Flush on unmount so a navigation right after the last activation
  // doesn't drop the gesture on the floor. (Mirrors the preferences
  // hook's unmount-flush pattern.)
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const body = pendingRef.current;
      pendingRef.current = null;
      if (body) mutateRef.current(body);
    };
  }, []);

  return {
    isPending: mutation.isPending,
    lastResponse: lastResponseRef.current,
    flush,
  };
}
